import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'
import { useToast } from '../../hooks/useToast'
import { Icon, Modal } from '../ui'
import { IPLP_FRAMEWORK } from '../../lib/framework'
import { componentAsOf, componentFromStructure, formVersionForYear } from '../../lib/versions'
import { useFormVersion } from '../../hooks/useFormVersion'
import { yearInfo } from '../../lib/types'
import type { Iplp, ComponentRating, Goal } from '../../lib/types'
import type { Json } from '../../lib/database.types'

interface ImportPanelProps {
  teacherId: string
  districtId: string
  fromYear: string // prior cycle to import from
  toYear: string // the empty planning year
  onImported: () => void
}

const LVL_NAME = ['—', 'Unsat.', 'Basic', 'Prof.', 'Disting.']

interface CarriedRating {
  code: string
  value: number
  title: string
  priorTitle: string
  wordingFixed: boolean
}

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim()

export function ImportPanel({ teacherId, districtId, fromYear, toYear, onImported }: ImportPanelProps) {
  const toast = useToast()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  const { data: source } = useQuery<Iplp | null>({
    queryKey: ['iplp', teacherId, fromYear],
    queryFn: async () => {
      const { data } = await supabase.from('iplps').select('*').eq('teacher_id', teacherId).eq('school_year', fromYear).single()
      return (data as Iplp) ?? null
    },
    enabled: !!teacherId,
  })

  const { data: srcRatings = [] } = useQuery<ComponentRating[]>({
    queryKey: ['ratings', source?.id],
    queryFn: async () => {
      if (!source?.id) return []
      const { data } = await supabase.from('component_ratings').select('*').eq('iplp_id', source.id)
      return (data as ComponentRating[]) ?? []
    },
    enabled: !!source?.id,
  })

  const { data: srcGoals = [] } = useQuery<Goal[]>({
    queryKey: ['goals', source?.id],
    queryFn: async () => {
      if (!source?.id) return []
      const { data } = await supabase.from('goals').select('*').eq('iplp_id', source.id)
      return (data as Goal[]) ?? []
    },
    enabled: !!source?.id,
  })

  const srcVersion = source?.form_version ?? formVersionForYear(fromYear)

  // For district-customized forms, wording comes from the immutable
  // published structures; the static Danielson versions are the fallback.
  const { pinned, template } = useFormVersion(source?.form_template_id, srcVersion)

  const currentTitleFor = (code: string): string | undefined =>
    componentFromStructure(template?.structure, code)?.title ??
    IPLP_FRAMEWORK.flatMap((d) => d.components).find((c) => c.code === code)?.title

  const priorTitleFor = (code: string): string | undefined =>
    componentFromStructure(pinned?.structure, code)?.title ??
    componentAsOf(code, srcVersion)?.title

  // Build the import plan from the prior year's self-reflection ratings.
  const carried: CarriedRating[] = []
  const wordingFixes: CarriedRating[] = []
  for (const r of srcRatings) {
    if (!r.self_rating || r.phase !== 'fall') continue
    const title = currentTitleFor(r.component_code)
    if (!title) continue
    const prior = priorTitleFor(r.component_code) ?? title
    const item: CarriedRating = {
      code: r.component_code,
      value: r.self_rating,
      title,
      priorTitle: prior,
      wordingFixed: prior !== title && norm(prior) === norm(title),
    }
    carried.push(item)
    if (item.wordingFixed) wordingFixes.push(item)
  }
  carried.sort((a, b) => a.code.localeCompare(b.code))

  const goalCount = srcGoals.filter((g) => g.building_goal || g.fall_goal).length
  const hasSurvey = !!(source?.section2 as { survey_reflection?: string } | undefined)?.survey_reflection
  const hasLeadership = ((source?.section3 as { leadership?: string[] } | undefined)?.leadership ?? []).length > 0

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!source) throw new Error('no source')
      // The new plan pins the template's latest published version, so its
      // responses render this year's wording even after future revisions.
      const newVersion =
        template && template.current_version > 0
          ? template.current_version
          : formVersionForYear(toYear)
      // Create the new year's plan, carrying forward sections but clearing scores/signatures.
      const { data: created, error } = await supabase
        .from('iplps')
        .insert({
          district_id: districtId,
          teacher_id: teacherId,
          evaluator_id: source.evaluator_id,
          form_template_id: source.form_template_id,
          school_year: toYear,
          current_phase: 'fall',
          form_version: newVersion,
          section1: {},
          section2: source.section2 as unknown as Json,
          section3: source.section3 as unknown as Json,
          section4: source.section4 as unknown as Json,
          section5: {},
          section6: {},
        })
        .select('id')
        .single()
      if (error || !created) throw error ?? new Error('insert failed')
      const newId = (created as { id: string }).id
      // Carry self-reflection ratings forward (scores stay blank).
      if (carried.length) {
        await supabase.from('component_ratings').insert(
          carried.map((c) => ({
            iplp_id: newId,
            district_id: districtId,
            component_code: c.code,
            phase: 'fall' as const,
            self_rating: c.value,
          }))
        )
      }
      // Carry goals forward.
      if (srcGoals.length) {
        await supabase.from('goals').insert(
          srcGoals.map((g) => ({
            iplp_id: newId,
            district_id: districtId,
            domain_number: g.domain_number,
            building_goal: g.building_goal,
            fall_goal: g.fall_goal,
            fall_action_steps: g.fall_action_steps,
          }))
        )
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['iplp', teacherId, toYear] })
      setOpen(false)
      toast(`Imported your ${yearInfo(fromYear).label} plan into ${yearInfo(toYear).label}`)
      onImported()
    },
    onError: () => toast('Could not import — connect a Supabase project to carry plans forward.'),
  })

  if (dismissed || !source || carried.length === 0) return null

  return (
    <>
      <div className="import-banner">
        <div className="ib-ic">
          <Icon name="refresh" size={20} />
        </div>
        <div className="ib-body">
          <div className="ib-title">Pick up where you left off?</div>
          <div className="ib-sub">
            Bring your <b>{yearInfo(fromYear).label}</b> work into <b>{yearInfo(toYear).label}</b> — {carried.length} component
            ratings, {goalCount} goal{goalCount === 1 ? '' : 's'}
            {hasLeadership ? ', leadership & survey' : ''}. Scores and signatures always start fresh.
          </div>
        </div>
        <div className="ib-actions">
          <button className="btn btn-ghost btn-sm" onClick={() => setDismissed(true)}>
            Start fresh
          </button>
          <button className="btn btn-pri btn-sm" onClick={() => setOpen(true)}>
            <Icon name="refresh" size={15} /> Review &amp; import
          </button>
        </div>
      </div>

      {open && (
        <Modal
          title={`Import from ${yearInfo(fromYear).label}`}
          icon="refresh"
          onClose={() => setOpen(false)}
          footer={
            <>
              <button className="btn" onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn btn-pri" disabled={importMutation.isPending} onClick={() => importMutation.mutate()}>
                <Icon name="check" size={16} /> Import into {yearInfo(toYear).label}
              </button>
            </>
          }
        >
          <p className="muted" style={{ marginTop: 0, fontSize: 13.5, lineHeight: 1.5 }}>
            This pre-fills your {yearInfo(toYear).label} plan with last year's self-reflection so you can update what's changed.
            Your evaluator's scores and all signatures stay blank until this year's cycle.
          </p>

          <div className="imp-summary">
            <div className="imp-sum-item">
              <Icon name="book" size={16} />
              <b>{carried.length}</b> component ratings
            </div>
            <div className="imp-sum-item">
              <Icon name="target" size={16} />
              <b>{goalCount}</b> growth goal{goalCount === 1 ? '' : 's'}
            </div>
            {hasLeadership && (
              <div className="imp-sum-item">
                <Icon name="award" size={16} />
                Leadership roles
              </div>
            )}
            {hasSurvey && (
              <div className="imp-sum-item">
                <Icon name="doc" size={16} />
                Survey reflection
              </div>
            )}
          </div>

          {wordingFixes.length > 0 && (
            <div className="imp-callout">
              <div className="imp-callout-h">
                <Icon name="pen" size={15} /> District updated the wording on {wordingFixes.length} component
                {wordingFixes.length === 1 ? '' : 's'}
              </div>
              <div className="imp-callout-sub">Your rating carries over; we'll use the district's current wording.</div>
              {wordingFixes.map((it) => (
                <div key={it.code} className="imp-word">
                  <span className="mono fb-code">{it.code}</span>
                  <span className="imp-word-old">{it.priorTitle}</span>
                  <Icon name="chevR" size={14} style={{ color: 'var(--ink-4)', flex: '0 0 auto' }} />
                  <span className="imp-word-new">{it.title}</span>
                </div>
              ))}
            </div>
          )}

          <div className="imp-list-h">Ratings carried forward</div>
          <div className="imp-ratings">
            {carried.map((it) => (
              <div key={it.code} className="imp-rate">
                <span className="mono fb-code">{it.code}</span>
                <span className="grow ell" style={{ fontSize: 13 }}>
                  {it.title}
                  {it.wordingFixed && <span className="imp-tag">updated</span>}
                </span>
                <span className={`imp-lvl lvl${it.value}`}>{LVL_NAME[it.value]}</span>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </>
  )
}
